// Étape 16 — Fiche lead (panneau latéral)
import { useState, useEffect } from "react";
import {
  C, fmtEur, Btn, Input, Textarea, Select, FormField, Alert, Spinner, Badge, fdate, fdatetime,
  leadsV2Api, errMsg, LEAD_SOURCES, LEAD_BESOINS, leadStatutLabel,
} from "./shared";

const MOTIFS = ["Pas intéressé", "Trop cher", "Déjà accompagné ailleurs", "Injoignable", "Reporté", "Autre"];
const Bloc = ({ titre, children, testid }) => (
  <div data-testid={testid} style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 10, padding: 14, marginBottom: 12 }}>
    <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 8 }}>{titre}</div>
    {children}
  </div>
);
const Kpi = ({ l, v, testid }) => (
  <div style={{ background: C.gray2, borderRadius: 8, padding: "8px 10px" }}>
    <div style={{ fontSize: 9, color: C.muted, textTransform: "uppercase", letterSpacing: ".05em" }}>{l}</div>
    <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }} data-testid={testid}>{v}</div>
  </div>
);

const Coordonnees = ({ lead, onSaved }) => {
  const i = lead.identite || {};
  const [f, setF] = useState({ prenom: i.prenom || "", nom: i.nom || "", telephone: i.telephone || "", email: i.email || "", besoin: lead.besoin || "", source: lead.source || "" });
  const [msg, setMsg] = useState("");
  const save = async () => { try { await leadsV2Api.update(lead.id, f); setMsg("Enregistré"); onSaved(); } catch (e) { setMsg(errMsg(e)); } };
  const set = (k, v) => setF({ ...f, [k]: v });
  return (
    <Bloc titre="Coordonnées" testid="lead-coordonnees">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <Input value={f.prenom} onChange={(e) => set("prenom", e.target.value)} placeholder="Prénom" data-testid="ld-prenom" />
        <Input value={f.nom} onChange={(e) => set("nom", e.target.value)} placeholder="Nom" data-testid="ld-nom" />
        <Input value={f.telephone} onChange={(e) => set("telephone", e.target.value)} placeholder="Téléphone" data-testid="ld-tel" />
        <Input value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="E-mail" data-testid="ld-email" />
        <Select value={f.besoin} onChange={(e) => set("besoin", e.target.value)} data-testid="ld-besoin">{LEAD_BESOINS.map(b => <option key={b}>{b}</option>)}</Select>
        <Select value={f.source} onChange={(e) => set("source", e.target.value)} data-testid="ld-source">{LEAD_SOURCES.map(b => <option key={b}>{b}</option>)}</Select>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
        <Btn small onClick={save} data-testid="ld-save">Enregistrer</Btn><span style={{ fontSize: 11, color: C.mid }}>{msg}</span>
      </div>
    </Bloc>
  );
};

const Actions = ({ lead, reload, onFixerRdv, onDemarrerBilan }) => {
  const [perte, setPerte] = useState(null);
  const [transfert, setTransfert] = useState(null);
  const [cibles, setCibles] = useState([]);
  const [err, setErr] = useState("");
  const run = async (fn) => { setErr(""); try { await fn(); reload(); setPerte(null); setTransfert(null); } catch (e) { setErr(errMsg(e)); } };
  const pending = lead.transfert?.statut === "en_attente";
  const openTransfert = async () => { setTransfert({ vers: "", motif: "" }); setCibles(await leadsV2Api.cibles(lead.id)); };
  return (
    <Bloc titre="Actions" testid="lead-actions">
      {err && <Alert type="error"><span>⚠</span><span data-testid="ld-action-error">{err}</span></Alert>}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <Btn small variant="gold" onClick={() => onFixerRdv(lead)} disabled={pending || lead.anonymise} data-testid="ld-fixer-rdv">📅 Fixer un rendez-vous</Btn>
        <Btn small variant="navy" onClick={() => onDemarrerBilan(lead)} disabled={pending || lead.anonymise} data-testid="ld-demarrer-bilan">▶ Démarrer le bilan</Btn>
        {lead.statut === "nouveau" && <Btn small onClick={() => run(() => leadsV2Api.statut(lead.id, { statut: "contacte" }))} disabled={pending} data-testid="ld-contacte">Marquer « Contacté »</Btn>}
        {lead.statut === "perdu" && <Btn small variant="teal" onClick={() => run(() => leadsV2Api.statut(lead.id, { statut: "contacte" }))} data-testid="ld-reactiver">Réactiver</Btn>}
        {!["perdu", "client"].includes(lead.statut) && <Btn small variant="red" onClick={() => setPerte({ motif: "", precision: "" })} disabled={pending} data-testid="ld-perdu">Marquer « Perdu »</Btn>}
        {!pending && !lead.anonymise && <Btn small onClick={openTransfert} data-testid="ld-transferer">⇄ Transférer</Btn>}
      </div>
      {pending && <div style={{ fontSize: 12, color: C.golddm, marginTop: 8 }} data-testid="ld-transfert-attente">Transfert vers {lead.transfert.vers} en attente de validation par AUPRÉA.</div>}
      {perte && (
        <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
          <Select value={perte.motif} onChange={(e) => setPerte({ ...perte, motif: e.target.value })} data-testid="ld-motif"><option value="">— Motif (obligatoire) —</option>{MOTIFS.map(m => <option key={m}>{m}</option>)}</Select>
          <Input value={perte.precision} onChange={(e) => setPerte({ ...perte, precision: e.target.value })} placeholder={perte.motif === "Autre" ? "Précision (obligatoire)" : "Précision (facultatif)"} data-testid="ld-motif-precision" />
          <Btn small variant="red" disabled={!perte.motif || (perte.motif === "Autre" && !perte.precision.trim())} onClick={() => run(() => leadsV2Api.statut(lead.id, { statut: "perdu", ...perte }))} data-testid="ld-perdu-confirm">Confirmer « Perdu »</Btn>
        </div>
      )}
      {transfert && (
        <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
          <Select value={transfert.vers} onChange={(e) => setTransfert({ ...transfert, vers: e.target.value })} data-testid="ld-transfert-vers">
            <option value="">— Vers (équipe N1 ou parrain) —</option>
            {cibles.map(c => <option key={c.id} value={c.id}>{c.nom} · {c.lien}</option>)}
          </Select>
          {!cibles.length && <div style={{ fontSize: 11, color: C.muted }}>Aucun membre d'équipe ni parrain disponible.</div>}
          <Input value={transfert.motif} onChange={(e) => setTransfert({ ...transfert, motif: e.target.value })} placeholder="Motif du transfert" data-testid="ld-transfert-motif" />
          <Btn small variant="gold" disabled={!transfert.vers || !transfert.motif.trim()} onClick={() => run(() => leadsV2Api.transferer(lead.id, transfert))} data-testid="ld-transfert-confirm">Demander le transfert</Btn>
        </div>
      )}
    </Bloc>
  );
};

const Notes = ({ lead, reload }) => {
  const [t, setT] = useState("");
  const add = async () => { if (!t.trim()) return; await leadsV2Api.note(lead.id, t); setT(""); reload(); };
  return (
    <Bloc titre="Notes" testid="lead-notes">
      {(lead.notes || []).slice().reverse().map((n, k) => <div key={k} style={{ fontSize: 12, padding: "6px 0", borderBottom: `.5px solid ${C.border}` }}><span style={{ color: C.muted, fontSize: 10 }}>{fdatetime(n.at)} · {n.par}</span><div>{n.texte}</div></div>)}
      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
        <Textarea value={t} onChange={(e) => setT(e.target.value)} placeholder="Ajouter une note…" style={{ minHeight: 40 }} data-testid="ld-note-input" />
        <Btn small onClick={add} data-testid="ld-note-add">Ajouter</Btn>
      </div>
    </Bloc>
  );
};

const AdminRgpd = ({ lead, onClose, onSaved }) => {
  const exp = async () => {
    const d = await leadsV2Api.exportRgpd(lead.id);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([JSON.stringify(d, null, 2)], { type: "application/json" }));
    a.download = `lead-${lead.id}.json`; a.click();
  };
  const anon = async () => { if (window.confirm("Anonymiser définitivement ce lead ?")) { await leadsV2Api.anonymiser(lead.id); onSaved(); onClose(); } };
  const del = async () => { if (window.confirm("Supprimer définitivement ce lead ?")) { await leadsV2Api.remove(lead.id); onSaved(); onClose(); } };
  return (
    <Bloc titre="RGPD (admin)" testid="lead-rgpd">
      <div style={{ display: "flex", gap: 6 }}>
        <Btn small onClick={exp} data-testid="ld-export">⬇ Exporter</Btn>
        <Btn small onClick={anon} data-testid="ld-anonymiser">Anonymiser</Btn>
        <Btn small variant="red" onClick={del} data-testid="ld-supprimer">Supprimer</Btn>
      </div>
    </Bloc>
  );
};

export const LeadDetail = ({ leadId, onClose, onSaved, isAdmin, onFixerRdv, onDemarrerBilan }) => {
  const [lead, setLead] = useState(null);
  const [err, setErr] = useState("");
  const load = () => leadsV2Api.get(leadId).then(setLead).catch(e => setErr(errMsg(e)));
  useEffect(() => { load(); }, [leadId]); // eslint-disable-line react-hooks/exhaustive-deps
  const reload = () => { load(); onSaved && onSaved(); };
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,.35)" }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div data-testid="lead-panel" style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: 520, maxWidth: "100%", background: C.gray, overflowY: "auto", boxShadow: "-8px 0 30px rgba(0,0,0,.15)", padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif" }} data-testid="lead-panel-nom">{lead?.prospect || "…"}</div>
            {lead && <div style={{ display: "flex", gap: 6, marginTop: 4, alignItems: "center" }}><span data-testid="lead-panel-statut"><Badge text={leadStatutLabel(lead)} /></span>{lead.a_relancer && <Badge text="À relancer" />}<span style={{ fontSize: 11, color: C.muted }}>{lead.id} · déclaré le {fdate(lead.date)}</span></div>}
          </div>
          <button onClick={onClose} data-testid="lead-panel-close" style={{ background: "none", border: "none", fontSize: 24, cursor: "pointer", color: C.muted }}>×</button>
        </div>
        {err && <Alert type="error"><span>⚠</span><span>{err}</span></Alert>}
        {!lead && !err && <Spinner />}
        {lead && (
          <>
            {lead.statut === "perdu" && <Alert type="error"><span>✕</span><span>Perdu — {lead.motif_perte}{lead.motif_precision ? ` : ${lead.motif_precision}` : ""}</span></Alert>}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 12 }}>
              <Kpi l="Signé" v={fmtEur(lead.montant_signe)} testid="ld-signe" />
              <Kpi l="Encaissé" v={fmtEur(lead.montant_encaisse)} testid="ld-encaisse" />
              <Kpi l={`Commission${lead.commission ? "" : lead.commission_detail ? " (potentielle)" : ""}`} v={lead.commission ? `${fmtEur(lead.commission)} · ${lead.commission_statuts.join(", ")}` : lead.commission_detail ? fmtEur(lead.commission_detail.montant) : "—"} testid="ld-commission" />
            </div>
            <Actions lead={lead} reload={reload} onFixerRdv={onFixerRdv} onDemarrerBilan={onDemarrerBilan} />
            {!lead.anonymise && <Coordonnees key={lead.id + (lead.identite?.nom || "")} lead={lead} onSaved={reload} />}
            <Bloc titre="Rendez-vous" testid="lead-rdvs">
              {!lead.rdvs.length && <div style={{ fontSize: 12, color: C.muted }}>Aucun rendez-vous.</div>}
              {lead.rdvs.map(r => <div key={r.id} style={{ fontSize: 12, padding: "4px 0" }}>📅 {fdate(r.date)} {r.heure} · {r.format === "visio" ? "visio" : "présentiel"} · {r.statut}</div>)}
            </Bloc>
            <Bloc titre="Dossiers liés" testid="lead-dossiers">
              {!lead.dossiers.length && <div style={{ fontSize: 12, color: C.muted }}>Aucun dossier.</div>}
              {lead.dossiers.map(d => <div key={d.id} style={{ fontSize: 12, padding: "4px 0" }}>📁 {d.id} · {d.type_parcours} · {d.statut}{d.numero ? ` · proposition n°${d.numero} (${d.proposition_statut})` : ""}{d.encaisse ? ` · ${fmtEur(d.encaisse)} encaissés` : ""}</div>)}
            </Bloc>
            <Notes lead={lead} reload={reload} />
            <Bloc titre="Historique" testid="lead-timeline">
              {(lead.timeline || []).slice().reverse().map((e, k) => (
                <div key={k} style={{ display: "flex", gap: 10, fontSize: 12, padding: "5px 0", borderLeft: `2px solid ${C.gold}`, paddingLeft: 10, marginLeft: 4 }}>
                  <span style={{ color: C.muted, minWidth: 110, fontSize: 11 }}>{fdatetime(e.at)}</span><span>{e.texte}</span>
                </div>
              ))}
            </Bloc>
            {isAdmin && <AdminRgpd lead={lead} onClose={onClose} onSaved={onSaved} />}
          </>
        )}
      </div>
    </div>
  );
};
